import React, { useState, useRef } from 'react';
import { 
  Search, 
  MessageCircle, 
  Phone, 
  Mail, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  Send, 
  CheckCircle, 
  AlertCircle, 
  FileText, 
  Server, 
  Headphones,
  Users,
  Zap,
  Shield,
  Globe
} from 'lucide-react';
import PublicSupportChat from './PublicSupportChat';

// Componente para FAQ expandível
const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-700/30 transition-colors"
      >
        <span className="text-white font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp size={20} className="text-purple-400 flex-shrink-0" />
        ) : (
          <ChevronDown size={20} className="text-purple-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6">
          <div className="text-gray-300 leading-relaxed">{answer}</div>
        </div>
      )}
    </div>
  );
};

// Componente para artigo da base de conhecimento
const KnowledgeBaseCard = ({ icon: Icon, title, description, category, readTime }) => {
  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6 border border-gray-700 hover:border-purple-500/50 transition-all duration-300 cursor-pointer group">
      <div className="flex items-start space-x-4">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="bg-purple-600/20 text-purple-300 text-xs px-2 py-1 rounded-full">
              {category}
            </span>
            <span className="text-gray-500 text-xs">• {readTime}</span>
          </div>
          <h3 className="text-white font-semibold mb-2 group-hover:text-purple-300 transition-colors">
            {title}
          </h3>
          <p className="text-gray-400 text-sm">{description}</p>
        </div>
      </div>
    </div>
  );
};

// Componente para canal de suporte
const SupportChannel = ({
  icon: Icon,
  title,
  description,
  availability,
  action,
  primary = false,
  href,
  onAction,
}) => {
  const actionClasses = `text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
    primary
      ? 'bg-purple-600 text-white hover:bg-purple-700'
      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
  }`;

  return (
    <div className={`p-6 rounded-xl border transition-all duration-300 hover:scale-105 ${
      primary 
        ? 'bg-gradient-to-br from-purple-600/20 to-indigo-600/20 border-purple-500/50 hover:border-purple-400' 
        : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
    }`}>
      <div className="flex items-start space-x-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
          primary 
            ? 'bg-gradient-to-br from-purple-500 to-indigo-600' 
            : 'bg-gray-700'
        }`}>
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold mb-1">{title}</h3>
          <p className="text-gray-400 text-sm mb-3">{description}</p>
          <div className="flex flex-col items-start gap-3">
            <span className="text-xs text-gray-500">{availability}</span>
            {href ? (
              <a href={href} className={`${actionClasses} max-w-full break-all`}>{action}</a>
            ) : (
              <button type="button" onClick={onAction} className={actionClasses}>
                {action}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente principal da página de suporte
const SupportPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    priority: 'medium',
    category: 'technical',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const searchRef = useRef(null);

  // FAQs mais comuns
  const faqs = [
    {
      question: "Como posso migrar meu site para a Hostul?",
      answer: "A migração gratuita está incluída nos planos Profissional e Hostul Max. Nossa equipe orienta a transferência de arquivos e bancos de dados; as condições e a disponibilidade são confirmadas pelo suporte antes do início."
    },
    {
      question: "Qual é o tempo de ativação do meu plano?",
      answer: "Novos planos são ativados automaticamente em até 15 minutos após a confirmação do pagamento. Você receberá um email com os dados de acesso ao seu painel de controle assim que a conta estiver pronta."
    },
    {
      question: "Como posso alterar meu plano de hospedagem?",
      answer: "Você pode fazer upgrade ou downgrade do seu plano a qualquer momento através do painel de controle. As alterações são aplicadas imediatamente e o valor é ajustado proporcionalmente na próxima fatura."
    },
    {
      question: "Vocês oferecem backup dos dados?",
      answer: "Sim. Os backups são semanais no plano Básico e diários nos planos Profissional e Hostul Max. As opções de retenção e restauração disponíveis para sua hospedagem são exibidas no painel ou podem ser confirmadas com o suporte."
    },
    {
      question: "Como configurar meu domínio para apontar para a Hostul?",
      answer: "Para apontar seu domínio, você precisa alterar os nameservers para: ns1.hostul.com.br e ns2.hostul.com.br. A propagação DNS pode levar até 48 horas. Temos um guia detalhado na base de conhecimento."
    },
    {
      question: "Posso instalar WordPress e outros CMS?",
      answer: "Claro! Oferecemos instalação automática com um clique para WordPress, Joomla, Drupal e mais de 400 aplicações. Também fornecemos otimizações específicas para WordPress, incluindo cache e CDN."
    }
  ];

  // Artigos da base de conhecimento
  const knowledgeBaseArticles = [
    {
      icon: Server,
      title: "Configurando seu primeiro site",
      description: "Guia completo para configurar e publicar seu primeiro site na Hostul",
      category: "Iniciante",
      readTime: "5 min"
    },
    {
      icon: Globe,
      title: "Gerenciamento de domínios",
      description: "Como registrar, transferir e configurar domínios",
      category: "Domínios",
      readTime: "8 min"
    },
    {
      icon: Mail,
      title: "Configuração de email profissional",
      description: "Criando e configurando contas de email personalizado",
      category: "Email",
      readTime: "6 min"
    },
    {
      icon: Shield,
      title: "Certificados SSL e segurança",
      description: "Instalando e configurando certificados SSL gratuitos",
      category: "Segurança",
      readTime: "4 min"
    },
    {
      icon: Zap,
      title: "Otimização de performance",
      description: "Dicas para acelerar seu site e melhorar o desempenho",
      category: "Performance",
      readTime: "10 min"
    },
    {
      icon: FileText,
      title: "Backup e restauração",
      description: "Como fazer backup manual e restaurar arquivos",
      category: "Backup",
      readTime: "7 min"
    }
  ];

  // Lidar com busca
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      console.log('Buscar por:', searchTerm);
      // Implementar lógica de busca aqui
    }
  };

  // Lidar com FAQ
  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Lidar com formulário de contato
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simular envio do formulário
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setContactForm({
        name: '',
        email: '',
        subject: '',
        priority: 'medium',
        category: 'technical',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-purple-500">
                <Server size={28} />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Hostul
              </span>
            </div>
            <div className="text-sm text-gray-400">
              Central de Suporte
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(#9f7aea_1px,transparent_1px)] [background-size:20px_20px] opacity-20"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Como podemos ajudar você?
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Encontre respostas rápidas, tutoriais detalhados ou entre em contato com nossa equipe especializada.
            </p>
            
            {/* Barra de busca */}
            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleSearch} className="relative">
                <input
                  ref={searchRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-4 px-6 pr-14 rounded-2xl bg-gray-800/50 backdrop-blur-sm border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
                  placeholder="Digite sua pergunta ou palavra-chave..."
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-2.5 rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                >
                  <Search size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Canais de Suporte */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Escolha como deseja ser atendido</h2>
            <p className="text-gray-300">Nossa equipe está sempre pronta para ajudar você</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <SupportChannel
              icon={MessageCircle}
              title="Chat ao Vivo"
              description="Converse com nossa equipe em tempo real"
              availability="24/7 disponível"
              action="Iniciar Chat"
              primary={true}
              onAction={() => setIsChatOpen(true)}
            />
            
            <SupportChannel
              icon={Phone}
              title="Telefone"
              description="Ligue para nosso suporte técnico"
              availability="Seg-Sex 8h às 18h"
              action="(27) 98841-6105"
              href="tel:+5527988416105"
            />
            
            <SupportChannel
              icon={Mail}
              title="Email"
              description="Envie sua dúvida detalhada por email"
              availability="Resposta em até 2h"
              action="suporte@hostul.com.br"
              href="mailto:suporte@hostul.com.br"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-900/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Perguntas Frequentes</h2>
            <p className="text-gray-300">Respostas para as dúvidas mais comuns</p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => toggleFAQ(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Base de Conhecimento */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Base de Conhecimento</h2>
            <p className="text-gray-300">Tutoriais e guias detalhados para resolver suas dúvidas</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {knowledgeBaseArticles.map((article, index) => (
              <KnowledgeBaseCard
                key={index}
                icon={article.icon}
                title={article.title}
                description={article.description}
                category={article.category}
                readTime={article.readTime}
              />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all">
              Ver Todos os Artigos
            </button>
          </div>
        </div>
      </section>

      {/* Formulário de Contato */}
      <section className="py-16 bg-gray-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Entre em Contato</h2>
              <p className="text-gray-300">Não encontrou o que procurava? Envie sua dúvida detalhada</p>
            </div>
            
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex items-center space-x-3">
                  <CheckCircle size={20} className="text-green-400" />
                  <span className="text-green-300">Mensagem enviada com sucesso! Nossa equipe entrará em contato em breve.</span>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center space-x-3">
                  <AlertCircle size={20} className="text-red-400" />
                  <span className="text-red-300">Erro ao enviar mensagem. Tente novamente ou entre em contato por outro canal.</span>
                </div>
              )}
              
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full py-3 px-4 rounded-lg bg-gray-700/50 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full py-3 px-4 rounded-lg bg-gray-700/50 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Assunto *
                  </label>
                  <input
                    type="text"
                    required
                    value={contactForm.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="w-full py-3 px-4 rounded-lg bg-gray-700/50 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Descreva brevemente sua dúvida"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Categoria
                    </label>
                    <select
                      value={contactForm.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="w-full py-3 px-4 rounded-lg bg-gray-700/50 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="technical">Suporte Técnico</option>
                      <option value="billing">Financeiro</option>
                      <option value="migration">Migração</option>
                      <option value="domain">Domínios</option>
                      <option value="email">Email</option>
                      <option value="other">Outros</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Prioridade
                    </label>
                    <select
                      value={contactForm.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      className="w-full py-3 px-4 rounded-lg bg-gray-700/50 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="low">Baixa</option>
                      <option value="medium">Média</option>
                      <option value="high">Alta</option>
                      <option value="urgent">Urgente</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mensagem *
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={contactForm.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="w-full py-3 px-4 rounded-lg bg-gray-700/50 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder="Descreva sua dúvida ou problema de forma detalhada..."
                  />
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        <span>Enviar Mensagem</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Informações de Contato */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-2xl p-8 border border-purple-500/30">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="flex flex-col items-center">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                  <Clock size={24} className="text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">Horário de Atendimento</h3>
                <p className="text-gray-300 text-sm">
                  Chat: 24/7<br />
                  Telefone: Seg-Sex 8h às 18h<br />
                  Email: Resposta em até 2h
                </p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                  <Headphones size={24} className="text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">Suporte Especializado</h3>
                <p className="text-gray-300 text-sm">
                  Equipe técnica certificada<br />
                  Respostas em português<br />
                  Suporte proativo
                </p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                  <Users size={24} className="text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">Satisfação Garantida</h3>
                <p className="text-gray-300 text-sm">
                  +10.000 clientes satisfeitos<br />
                  Nota 4.9/5 em avaliações<br />
                  99.9% de tempo de resposta
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="text-purple-500">
                <Server size={24} />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 text-transparent bg-clip-text">
                Hostul
              </span>
            </div>
            
            <div className="text-center text-gray-400 text-sm">
              <p>&copy; 2026 Hostul. Todos os direitos reservados.</p>
              <p className="mt-1">Suporte técnico especializado 24/7</p>
            </div>
            
            <div className="flex space-x-4 mt-4 md:mt-0">
              <button className="text-gray-400 hover:text-purple-400 transition-colors">
                Política de Privacidade
              </button>
              <button className="text-gray-400 hover:text-purple-400 transition-colors">
                Termos de Uso
              </button>
            </div>
          </div>
        </div>
      </footer>
      <PublicSupportChat
        open={isChatOpen}
        onOpen={() => setIsChatOpen(true)}
        onClose={() => setIsChatOpen(false)}
      />
    </div>
  );
};

export default SupportPage;
